import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGetAllGymsQuery } from "../slices/gymSlice";
import { getCurrentDate } from "../utils/getCurrentDate";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { openWhatsApp } from "../utils/generalUtilities";
import { IoLogoWhatsapp } from "react-icons/io";

const AllGymDetails = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [getApiQuery, setGetApiQuery] = useState(
    `?createdAt[gte]=${startDate}&createdAt[lte]=${endDate}&sort=createdAt`
  );
  const [gymStatusMap, setGymStatusMap] = useState({});
  const [substypeWiseCount, setSubstypeWiseCount] = useState({});
  const [gymsToDisplay, setGymsToDisplay] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchBy, setSearchBy] = useState("Gym Id");
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();
  const [gymStatusWiseCount, setGymStatusWiseCount] = useState({});

  let {
    data: allGyms,
    isLoading,
    error,
    refetch,
  } = useGetAllGymsQuery({
    token: userInfo?.token,
    query: getApiQuery,
  });

  useEffect(() => {
    refetch();
  }, [getApiQuery]);

  useEffect(() => {
    if (!allGyms) return;

    const statusMap = {};
    const subsMap = {};
    const gymStatusMapApi = {};

    allGyms.forEach((gym) => {
      statusMap[gym._id] =
        gym.subscriptionType === "Free"
          ? "FREE"
          : gym.subsExpiryDate && new Date(gym.subsExpiryDate) < new Date()
          ? "EXPIRED"
          : "ACTIVE";

      const subType = gym.subscriptionType || "Unknown";
      subsMap["Total"] = allGyms;

      if (!subsMap[subType]) {
        subsMap[subType] = [];
      }

      subsMap[subType].push(gym);

      const gymStatus = gym.gymStatus || "Unknown";

      gymStatusMapApi["Total"] = allGyms;

      if (!gymStatusMapApi[gymStatus]) {
        gymStatusMapApi[gymStatus] = [];
      }

      gymStatusMapApi[gymStatus].push(gym);
    });

    setGymStatusMap(statusMap);
    setSubstypeWiseCount(subsMap);
    setGymStatusWiseCount(gymStatusMapApi);
    setGymsToDisplay(allGyms);
  }, [allGyms]);

  useEffect(() => {
    if (!allGyms) return;

    let gyms = applyStatusFilter(allGyms, statusFilter);

    if (searchValue.trim()) {
      const value = searchValue.toLowerCase();

      gyms = gyms.filter((gym) => {
        switch (searchBy) {
          case "Gym Name":
            return gym.name?.toLowerCase().includes(value);

          case "Address":
            return gym.address?.toLowerCase().includes(value);

          default:
            return String(gym._id || "")
              .toLowerCase()
              .includes(value);
        }
      });
    }

    setGymsToDisplay(gyms);
  }, [searchValue, searchBy, statusFilter, allGyms]);

  const applyStatusFilter = (gyms, filter) => {
    if (!gyms) return [];

    if (filter === "ALL") return gyms;

    return gyms.filter((gym) => gymStatusMap[gym._id] === filter);
  };

  const handleSubscriptionFilter = (type) => {
    const gyms = type === "Total" ? allGyms : substypeWiseCount[type] || [];

    setGymsToDisplay(applyStatusFilter(gyms, statusFilter));
  };

  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);

    if (filter === "ALL") {
      setGymsToDisplay(allGyms);
      return;
    }

    const filteredGyms = allGyms.filter(
      (gym) => gymStatusMap[gym._id] === filter
    );

    setGymsToDisplay(filteredGyms);
  };

  const handleRenewSubscription = async (customer) => {
    navigate("/renewSubscription", {
      state: { gymDetails: customer },
    });
  };

  const handleGetCustomers = async () => {
    if (startDate > getCurrentDate())
      toast.error("Start date can not be greater than today");
    else if (startDate > endDate)
      toast.error("Start date can not be greater than End date");
    else {
      setGetApiQuery(
        `?createdAt[gte]=${startDate}&createdAt[lte]=${endDate}&sort=createdAt`
      );
    }
  };

  const handlePayNow = async (customer) => {
    navigate("/payNow", {
      state: { gymDetails: customer },
    });
  };

  const handleGymStatusApiFilter = (status) => {
    const gyms =
      status === "Total" ? allGyms : gymStatusWiseCount[status] || [];

    setGymsToDisplay(applyStatusFilter(gyms, statusFilter));
  };

  return (
    <>
      <label>
        <strong>All Gym Details</strong>
      </label>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message>{error.data.error}</Message>
      ) : (
        <>
          {(gymStatusWiseCount["Suspected_Duplicate"] || []).length > 0 && (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  color: "red",
                  fontSize: "1rem",
                }}
              >
                {`${
                  gymStatusWiseCount["Suspected_Duplicate"].length
                } Suspected Duplicate Gym${
                  gymStatusWiseCount["Suspected_Duplicate"].length > 1
                    ? "s"
                    : ""
                } Found.`}
              </span>

              <Button
                variant="warning"
                onClick={() => handleGymStatusApiFilter("Suspected_Duplicate")}
              >
                Click to View
              </Button>
            </span>
          )}
          <Row className="justify-content-center align-items-center mb-4 g-3">
            <Col xs="auto">
              <Form.Group controlId="visitorMobile">
                <Form.Control
                  type="date"
                  placeholder="2023-11-23"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                ></Form.Control>{" "}
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Form.Group controlId="visitorMobile">
                <Form.Control
                  type="date"
                  placeholder="2023-11-27"
                  value={endDate?.split("T")[0]}
                  onChange={(e) => setEndDate(e.target.value)}
                ></Form.Control>{" "}
              </Form.Group>
            </Col>
            <Col xs="auto" className="d-flex justify-content-center">
              <Form.Group className="my-1" controlId="gymType">
                <Button onClick={handleGetCustomers}>Get Gyms</Button>
              </Form.Group>
            </Col>
          </Row>
          <Row className="justify-content-center mb-1 g-3">
            <Col xs="auto">
              <Button onClick={() => handleStatusFilter("ALL")}>All</Button>
            </Col>

            <Col xs="auto">
              {" "}
              <Button
                style={{ backgroundColor: "green" }}
                onClick={() => handleStatusFilter("ACTIVE")}
              >
                Active
              </Button>
            </Col>

            <Col xs="auto">
              <Button
                style={{ backgroundColor: "red" }}
                onClick={() => handleStatusFilter("EXPIRED")}
              >
                Expired
              </Button>
            </Col>

            <Col xs="auto">
              <Button
                style={{ backgroundColor: "blue" }}
                onClick={() => handleStatusFilter("FREE")}
              >
                Free
              </Button>
            </Col>
            <Col xs="auto">
              <Form.Select
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
              >
                <option>Gym Id</option>
                <option>Gym Name</option>
                <option>Address</option>
              </Form.Select>
            </Col>

            <Col xs={9} md="auto">
              <Form.Control
                type="text"
                placeholder={`Search by ${searchBy}`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Col>
          </Row>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "2.5rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            {Object.keys(substypeWiseCount).map((key) => (
              <span
                key={key}
                onClick={() => handleSubscriptionFilter(key)}
                style={{
                  color: "green",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {key} :
                {
                  applyStatusFilter(substypeWiseCount[key] || [], statusFilter)
                    .length
                }
              </span>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "2.5rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            {Object.keys(gymStatusWiseCount).map((key) => (
              <span
                key={key}
                onClick={() => handleGymStatusApiFilter(key)}
                style={{
                  color: "blue",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {key} :{" "}
                {
                  applyStatusFilter(gymStatusWiseCount[key] || [], statusFilter)
                    .length
                }
              </span>
            ))}
          </div>
          <div
            style={{
              width: "100%",
              maxHeight: "70vh",
              overflowX: "auto",
            }}
          >
            <Table
              striped
              bordered
              hover
              responsive="sm"
              style={{ marginTop: "1rem" }}
            >
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Gym Id</th>
                  <th style={{ textAlign: "left" }}>Name</th>
                  {/* <th>Pincode</th> */}
                  <th style={{ textAlign: "left" }}>Mobile </th>
                  <th style={{ textAlign: "left" }}>Address</th>
                  {/* <th>Contacted Via</th> */}
                  <th style={{ textAlign: "left" }}>Joined</th>
                  <th>Renewed</th>
                  <th>Expiry</th>
                  <th style={{ textAlign: "left" }}>Att</th>
                  <th style={{ textAlign: "left" }}>Subs</th>
                  <th style={{ textAlign: "left" }}>WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {allGyms ? (
                  gymsToDisplay?.map((customer) => (
                    <tr>
                      <td>
                        {customer._id} <br />
                        <br />
                        {customer.dueAmount > 0 && (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.875rem",
                            }}
                          >
                            ₹{customer.dueAmount}
                          </span>
                        )}
                        <br />
                        {customer.dueAmount > 0 ? (
                          <span
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color:
                                gymStatusMap[customer._id] === "EXPIRED"
                                  ? "red"
                                  : gymStatusMap[customer._id] === "FREE"
                                  ? "blue"
                                  : "green",
                            }}
                            onClick={() => handlePayNow(customer)}
                          >
                            Pay Now
                          </span>
                        ) : (
                          <span
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color:
                                gymStatusMap[customer._id] === "EXPIRED"
                                  ? "red"
                                  : gymStatusMap[customer._id] === "FREE"
                                  ? "blue"
                                  : "green",
                            }}
                            onClick={() => handleRenewSubscription(customer)}
                          >
                            Renew
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        <span
                          style={{
                            color: "#0d6efd",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: "500",
                          }}
                          onClick={() =>
                            navigate(`/gymProfile/${customer._id}`)
                          }
                        >
                          {customer.name}
                        </span>
                      </td>
                      {/* <td>{customer.pincode}</td> */}
                      <td>
                        <a href={`tel:${customer.primaryContact}`}>
                          {customer.primaryContact}{" "}
                        </a>{" "}
                        <br /> <br />
                        <button
                          style={{
                            border: "none",
                            color: "green",
                            background: "transparent",
                            padding: "0px",
                            cursor: "pointer",
                            margin: "0px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={(e) =>
                            openWhatsApp(
                              customer.primaryContact,
                              gymStatusMap[customer._id] === "EXPIRED"
                                ? "expiredMembersMessage"
                                : customer.dueAmount > 0
                                ? "pendingPaymentsMessage"
                                : "otherMessage",
                              customer.dueAmount,
                              new Date(customer.subsExpiryDate)
                                ?.toISOString()
                                ?.split("T")[0],
                              customer.name
                            )
                          }
                        >
                          <IoLogoWhatsapp />
                        </button>
                      </td>
                      <td style={{ textAlign: "left" }}>{customer.address}</td>
                      {/* <td>{customer.reachedThrough}</td> */}
                      <td style={{ whiteSpace: "nowrap", textAlign: "left" }}>
                        {customer.createdAt
                          ? customer.createdAt?.split("T")[0]
                          : ""}
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left" }}>
                        {customer.subsRenewedDate
                          ? customer.subsRenewedDate?.split("T")[0]
                          : ""}
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left" }}>
                        {customer.subsExpiryDate
                          ? customer.subsExpiryDate?.split("T")[0]
                          : ""}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {customer.attendanceCount}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {customer.subsCount}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {customer.WsMessageCount}
                      </td>
                    </tr>
                  ))
                ) : (
                  <Message>No visitors Available</Message>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </>
  );
};

export default AllGymDetails;
